import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PaymentSummary from '../components/payment/PaymentSummary';
import TossPayment from '../components/payment/TossPayment';
import { useBookingStore } from '../stores/bookingStore';
import { setSEO } from '../utils/seo';
import { reservationApi } from '../api/reservationApi';

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    startDateTime,
    endDateTime,
    selectedVehicle,
    reservationData,
    paymentBreakdown,
    selectedCoupon,
  } = useBookingStore();

  const [tempReservationNumber, setTempReservationNumber] = React.useState<string>('');
  const [isLoadingTempNumber, setIsLoadingTempNumber] = React.useState(true);

  // Set page title
  React.useEffect(() => {
    window.scrollTo(0, 0);
    setSEO('payment');
  }, []);

  // Generate temporary reservation number for orderId
  React.useEffect(() => {
    const generateTempNumber = async () => {
      try {
        const number = await reservationApi.generateTempReservationNumber();
        setTempReservationNumber(number);
      } catch (error) {
        console.error('Failed to generate temp reservation number:', error);
        // Fallback to timestamp-based orderId
        setTempReservationNumber(`TEMP_${Date.now()}`);
      } finally {
        setIsLoadingTempNumber(false);
      }
    };

    if (startDateTime && endDateTime && selectedVehicle && reservationData) {
      generateTempNumber();
    }
  }, [startDateTime, endDateTime, selectedVehicle, reservationData]);

  // Redirect if incomplete booking data
  React.useEffect(() => {
    if (!startDateTime || !endDateTime || !selectedVehicle || !reservationData) {
      navigate('/checkout');
    }
  }, [startDateTime, endDateTime, selectedVehicle, reservationData, navigate]);

  if (!startDateTime || !endDateTime || !selectedVehicle || !reservationData) {
    return null;
  }

  // PaymentSummary에서 계산한 breakdown을 사용하여 최종 금액 계산
  const amountBeforeDiscount = paymentBreakdown
    ? paymentBreakdown.rental_fee +
      paymentBreakdown.insurance_fee +
      paymentBreakdown.delivery_fee +
      (paymentBreakdown.equipment_fee || 0)
    : 0;

  // 쿠폰 할인 적용
  const couponDiscount = selectedCoupon ? selectedCoupon.discount_amount : 0;
  const finalAmount = Math.max(0, amountBeforeDiscount - couponDiscount);

  const handlePaymentSuccess = (paymentKey: string, orderId: string, amount: number) => {
    console.log('Payment success:', { paymentKey, orderId, amount });
    // Toss Payments will redirect to /payment/success
  };

  const handlePaymentError = (error: Error) => {
    console.error('Payment error:', error);
    alert(t('payment.paymentError', { message: error.message }));
  };

  const handleBack = () => {
    navigate(-1); // 브라우저 히스토리 뒤로가기
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-500 shadow-md">
        <div className="container mx-auto px-4 py-3">
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between relative">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <div className="flex flex-col items-start">
                <span className="text-sm text-white/90 font-semibold">{t('flow.reservationInfo')}</span>
              </div>
            </button>
            <h1 className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2">{t('flow.payment')}</h1>
            <div className="w-20"></div>
          </div>

          {/* Mobile Header */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={handleBack}
              className="flex items-center text-white/90 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex items-center gap-2 flex-1">
              <img src="/logo.svg" alt="굿타임 렌터카" className="h-7" />
              <h1 className="text-lg font-semibold text-white truncate">{t('flow.payment')}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-4 md:py-12">
        <div className="max-w-6xl mx-auto">
          {/* Progress Indicator - Desktop */}
          <div className="mb-8 hidden md:block">
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    ✓
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.dateSelect')}</span>
                </div>
                <div className="w-16 h-1 bg-primary-600 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    ✓
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.vehicleSelect')}</span>
                </div>
                <div className="w-16 h-1 bg-primary-600 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    ✓
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.reservationInfo')}</span>
                </div>
                <div className="w-16 h-1 bg-primary-600 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    4
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.payment')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator - Mobile Simplified */}
          <div className="mb-4 md:hidden">
            <div className="flex items-center justify-center gap-1.5">
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  ✓
                </div>
                <span className="text-[10px] font-medium text-gray-700">{t('flow.date')}</span>
              </div>
              <div className="w-4 h-0.5 bg-primary-600"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  ✓
                </div>
                <span className="text-[10px] font-medium text-gray-700">{t('flow.vehicle')}</span>
              </div>
              <div className="w-4 h-0.5 bg-primary-600"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  ✓
                </div>
                <span className="text-[10px] font-medium text-gray-700">{t('flow.reservation')}</span>
              </div>
              <div className="w-4 h-0.5 bg-primary-600"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  4
                </div>
                <span className="text-[10px] font-medium text-gray-900">{t('flow.payment')}</span>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {/* Left - Payment Summary */}
            <div className="order-1 md:order-1">
              <PaymentSummary
                vehicle={selectedVehicle}
                startDateTime={startDateTime}
                endDateTime={endDateTime}
                userName={reservationData.name}
                userPhone={reservationData.phone}
                pickupLocationInfo={reservationData.pickupLocationInfo}
                returnLocationInfo={reservationData.returnLocationInfo}
                selectedInsuranceId={reservationData.selectedInsuranceId}
                selectedEquipmentIds={reservationData.selectedEquipmentIds}
                showCouponSelector={true}
              />
            </div>

            {/* Right - Toss Payment */}
            <div className="order-2 md:order-2">
              {isLoadingTempNumber ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t('payment.paymentPreparing')}</p>
                </div>
              ) : (
                <TossPayment
                  amount={finalAmount}
                  orderId={`ORD_${tempReservationNumber}_${Date.now()}`}
                  orderName={t('payment.orderName', { vehicleName: selectedVehicle.model_name })}
                  customerName={reservationData.name}
                  customerEmail={reservationData.email}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
